import { Component } from 'preact';
import { toast } from 'sonner';
import { CloseButton } from '../ui/navButtons';
import ForgedButton from '../ui/ForgedButton';
import { RADIUS, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, OPAQUE_PANEL_BG, GOLD } from '../../lib/medievalTheme';
import { getClass } from '../../utils/arena/classes';
import { getSkillState } from '../../utils/arena/skills';
import {
  ALCHEMIST_POTIONS, HUMOURS, HUMOUR_BY_KEY, POTION_RECIPES,
  potionArt, potionArtMask, canBrew, canDrop, potionStock, brewPotion,
  POTION_POTENCIES, potionPotency, potionCost, canBrewPotency, potionEffect,
} from '../../utils/arena/alchemist';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { t } from '../../localization/t';
import ElementIcon from '../deckStats/ElementIcon';
import LaboratoryBackdrop from './LaboratoryBackdrop';
import PotionPaint from './PotionPaint';
import PotionArtwork from './PotionArtwork';
import { POTION_LIQUID } from './potionLiquid';

const CLS = getClass('alchemist');
const ACCENT = CLS.accent;
const SHORT = '#e89090';
const potionName = (potion, potency) => t('{potency} Potion of {name}', { potency: t(potionPotency(potency).title), name: t(potion.title) });

// Every candidate shares a grid cell, so the longest translated text reserves
// its real wrapped height without measuring after each selection.
function ReservedText({ candidates, children, testId, className = '', style = {} }) {
  return (
    <div className={className} style={{ display: 'grid', width: '100%', ...style }}>
      {candidates.map((text, i) => <div key={i} aria-hidden style={{ gridArea: '1 / 1', visibility: 'hidden', pointerEvents: 'none' }}>{text}</div>)}
      <div style={{ gridArea: '1 / 1' }} data-testid={testId}>{children}</div>
    </div>
  );
}

// What the potion is to this player: brewable, drop-only, or still locked
// on the tree. The list orders by that so the working shelf reads first.
function potionState(skills, alchemist, key, potency = 'ordinary') {
  const recipe = canBrew(skills, key);
  const drop = canDrop(skills, key);
  const potencyUnlocked = canBrewPotency(skills, potency);
  const stock = potionStock(alchemist, key, potency);
  const uses = (alchemist?.active?.[key]?.potency || 'ordinary') === potency ? alchemist?.active?.[key]?.uses || 0 : 0;
  return { recipe, drop, potencyUnlocked, known: ((recipe || drop) && potencyUnlocked) || stock > 0 || uses > 0, stock, uses };
}

function affordable(alchemist, key, potency) {
  return Object.entries(potionCost(key, potency)).every(([h, n]) => (alchemist?.[h] || 0) >= n);
}

// One humour on the ledger: its Sorcery element pip (the same token the
// tree's quadrants wear) and the count.
function HumourCount({ humour, value, short, size = 1, labelColor = TEXT_MUTED }) {
  return (
    <span className="flex items-center" style={{ gap: `${0.45 * size}cqw` }}>
      <ElementIcon element={humour.element} size={17 * size} />
      <span className="arena-heading tabular-nums" style={{ color: short ? SHORT : TEXT_PRIMARY, fontSize: `${0.875 * size}cqw` }}>{value}</span>
      <span style={{ color: labelColor, fontSize: `${0.75 * size}cqw` }}>{t(humour.label)}</span>
    </span>
  );
}

function PotionRow({ potion, state, alchemist, potency, selected, drawSelection, onSelect, onHover }) {
  const { known, stock, uses, recipe, potencyUnlocked } = state;
  const paintSeed = potionName(potion, potency);
  return (
    <button
      type="button"
      data-testid={`potion-row-${potion.key}-${potency}`}
      data-selected={selected}
      onClick={() => onSelect(potion.key, potency)}
      onMouseEnter={onHover}
      aria-pressed={selected}
      className={`vh-lab-row relative flex items-center shrink-0 w-full text-left cursor-pointer${selected ? ' vh-lab-row-selected' : ''}`}
      style={{
        gap: '0.85cqw', padding: '0.35cqw 0.75cqw',
        '--row-ink': selected ? '#23201b' : known ? '#e9decb' : '#978e81',
        '--row-muted': selected ? '#51483c' : '#978e81',
        '--row-cost': selected ? '#23201b' : '#e9decb',
        '--row-short': selected ? '#8c342d' : SHORT,
        '--row-active': selected ? '#694477' : ACCENT,
      }}
    >
      <PotionPaint seed={paintSeed} className="vh-lab-row-wash" />
      {selected ? <PotionPaint key={potency} seed={paintSeed} className="vh-lab-row-selection" draw={drawSelection} /> : null}
      <span className="relative shrink-0 flex items-center justify-center" style={{ width: '4.5cqw', height: '4.5cqw' }}>
        <img src={potionArt(potion.key)} alt="" draggable={false} className="relative"
          style={{ width: '100%', height: '100%', objectFit: 'contain', maskImage: potionArtMask(potion.key), maskSize: '100% 100%', filter: known ? 'none' : 'grayscale(0.7)', opacity: known ? 1 : 0.5 }} />
      </span>
      <span className="relative flex flex-col min-w-0 flex-1" style={{ gap: '0.125cqw' }}>
        <span className="vh-lab-row-name arena-heading relative inline-flex self-start max-w-full" style={{ padding: '0.15cqw 0.4cqw', fontSize: '1.3cqw', letterSpacing: '0.02em' }}>
          <span className="relative whitespace-nowrap" style={{ lineHeight: 1.3 }}>{potionName(potion, potency)}</span>
        </span>
        <span className="truncate" style={{ color: 'var(--row-muted)', fontSize: '0.85cqw', paddingLeft: '0.4cqw' }}>
          {!potencyUnlocked ? t('{n} Alchemist perks required', { n: potionPotency(potency).perks }) : !known ? t('Not unlocked') : !recipe ? t('Found, not brewed') : t(POTION_RECIPES[potion.key].kind === 'match' ? 'Drink before a match' : 'Drink before opening a booster')}
        </span>
      </span>
      <span className="relative flex flex-col items-center shrink-0 tabular-nums" style={{ width: '4.5cqw', gap: '0.35cqw' }}>
        <span className="arena-heading" style={{ color: 'var(--row-cost)', fontSize: '1.3cqw' }} data-testid={`potion-owned-${potion.key}-${potency}`}>{stock}</span>
        {uses > 0 ? <span style={{ color: 'var(--row-active)', fontSize: '0.8cqw' }}>{t('Ready')}</span> : null}
      </span>
      <span className="relative flex flex-col shrink-0" style={{ width: '8.5cqw', gap: '0.35cqw' }}>
        <span className="flex items-center" style={{ gap: '0.75cqw' }} data-testid={`potion-cost-${potion.key}-${potency}`}>
          {Object.entries(potionCost(potion.key, potency)).map(([humourKey, amount]) => {
            const humour = HUMOUR_BY_KEY[humourKey];
            const short = (alchemist?.[humourKey] || 0) < amount;
            return (
              <span key={humourKey} className="flex items-center" style={{ width: '3.75cqw', gap: '0.4cqw' }}
                aria-label={`${amount} ${t(humour.label)}`} data-short={short}>
                <ElementIcon element={humour.element} size={24} />
                <span className="arena-heading tabular-nums" style={{ color: short ? 'var(--row-short)' : 'var(--row-cost)', fontSize: '1.2cqw' }}>{amount}</span>
              </span>
            );
          })}
        </span>
      </span>
    </button>
  );
}

// The Alchemist's laboratory: the shelf of potions on the left, the chosen
// bottle large on the right with its effect, its words and its recipe, the
// humour ledger at the foot and the alembic's two actions off it.
export default class LaboratoryScreen extends Component {
  state = { selected: null, potency: 'ordinary', filter: 'all', filterClicked: false, drawSelection: false, busy: null, brewFx: null, brewWaves: [] };
  _waveTimers = new Set();

  componentDidMount() {
    this._esc = pushDismiss(this.props.onClose);
    playUI(UI.OPEN);
  }

  componentWillUnmount() {
    this._unmounted = true;
    clearTimeout(this._brewTimer);
    for (const timer of this._waveTimers) clearTimeout(timer);
    if (this._esc != null) removeDismiss(this._esc);
  }

  get skills() { return this.props.profile?.skills || getSkillState(); }
  get alchemist() { return this.props.profile?.alchemist || {}; }

  // Potions the player can act on come first; the chosen one defaults to
  // the first of those.
  get ordered() {
    const skills = this.skills;
    const rank = (p) => (canBrew(skills, p.key) ? 0 : canDrop(skills, p.key) ? 1 : 2);
    return ALCHEMIST_POTIONS.map((p, i) => ({ p, i })).sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i).map((x) => x.p);
  }

  get selectedKey() {
    return this.state.selected || this.ordered[0].key;
  }

  select = (key, potency) => {
    if (key === this.selectedKey && potency === this.state.potency) return;
    this.cancelBrewGlow();
    playUI(UI.UNEQUIP);
    this.setState({ selected: key, potency, drawSelection: true });
  };

  filter = (filter) => {
    if (filter === this.state.filter) return;
    this.cancelBrewGlow();
    playUI(UI.EQUIP);
    this.setState({ filter, filterClicked: true, drawSelection: false, ...(filter === 'all' ? {} : { potency: filter }) }, () => {
      if (this._list) this._list.scrollTop = 0;
    });
  };

  hover = () => playUI(UI.HOVER, { volume: 0.3 });

  cancelBrewGlow = () => {
    this._selectionRevision = (this._selectionRevision || 0) + 1;
    clearTimeout(this._brewTimer);
    for (const timer of this._waveTimers) clearTimeout(timer);
    this._waveTimers.clear();
    this.setState({ brewFx: null, brewWaves: [] });
  };

  brew = async () => {
    const key = this.selectedKey;
    if (this.state.busy) return;
    const revision = this._selectionRevision || 0;
    this.setState({ busy: 'brew' });
    try {
      const next = await brewPotion(key, this.state.potency);
      if (this._unmounted) return;
      playUI(UI.BREW);
      this.props.onAlchemist?.(next);
      let brewFx = null;
      if (revision === (this._selectionRevision || 0)) {
        const wall = this._wall.getBoundingClientRect();
        const bottle = this._bottle.getBoundingClientRect();
        // Convert the bottle's foot into the backdrop's centred, cover-scaled SVG.
        const scale = Math.max(wall.width / 1600, wall.height / 900) || 1;
        brewFx = {
          id: (this._brewId = (this._brewId || 0) + 1), potion: key,
          color: POTION_LIQUID[key].color,
          x: (bottle.left + bottle.width / 2 - wall.left - (wall.width - 1600 * scale) / 2) / scale,
          y: (bottle.top + bottle.height * 0.94 - wall.top - (wall.height - 900 * scale) / 2) / scale,
        };
      }
      clearTimeout(this._brewTimer);
      this.setState(previous => ({
        busy: null, brewFx,
        // Keep recent pulses independent, with bounded rendering during rapid crafting.
        brewWaves: brewFx ? [...previous.brewWaves, brewFx].slice(-6) : previous.brewWaves,
      }));
      if (brewFx) {
        this._brewTimer = setTimeout(() => this.setState({ brewFx: null }), 3000);
        const timer = setTimeout(() => {
          this._waveTimers.delete(timer);
          this.setState(previous => ({ brewWaves: previous.brewWaves.filter(wave => wave.id !== brewFx.id) }));
        }, 3000);
        this._waveTimers.add(timer);
      }
    } catch (err) {
      if (this._unmounted) return;
      playUI(UI.ERROR);
      this.setState({ busy: null });
      toast.error(err?.status === 402 ? t('Not enough humours for that recipe.') : t('The brew failed. Try again.'));
    }
  };

  render() {
    const { onClose } = this.props;
    const { busy, potency, filter, filterClicked } = this.state;
    const skills = this.skills;
    const alchemist = this.alchemist;
    const key = this.selectedKey;
    const potion = ALCHEMIST_POTIONS.find((p) => p.key === key);
    const state = potionState(skills, alchemist, key, potency);
    const tier = potionPotency(potency);
    const cost = potionCost(key, potency);
    const potencyUnlocked = canBrewPotency(skills, potency);
    const canAfford = affordable(alchemist, key, potency);
    const brewable = state.recipe && potencyUnlocked && canAfford && !busy;

    return (
      <div ref={(node) => { this._wall = node; }} className="absolute inset-0 flex flex-col" style={{ '--vh-class-accent': ACCENT, background: OPAQUE_PANEL_BG }} data-testid="laboratory">
        <LaboratoryBackdrop waves={this.state.brewWaves} />

        <div className="relative shrink-0 flex items-start justify-between px-12 pt-10">
          <div>
            <div className="arena-heading font-bold" style={{ color: ACCENT, fontSize: '2cqw', textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>{t('Laboratory')}</div>
          </div>
          {/* The four humours, large, where the eye goes first. */}
          <div className="flex items-center" style={{ gap: '2.25cqw', paddingTop: '0.375cqw' }}>
            <div className="flex items-center" style={{ gap: '2cqw' }} data-testid="humour-ledger">
              {HUMOURS.map((h) => <HumourCount key={h.key} humour={h} value={alchemist?.[h.key] || 0} size={1.5} />)}
            </div>
            <CloseButton title={t('Close')} onClick={onClose} />
          </div>
        </div>

        <div className="relative flex-1 min-h-0 flex px-12 pb-10 pt-6" style={{ gap: '3cqw' }}>
          {/* The recipe ledger: generous rows and a shared cost column. */}
          <div className="flex flex-col shrink-0 min-h-0" style={{ width: '47cqw' }} data-testid="potion-shelf">
            <div role="group" aria-label={t('Filter potion potency')} className="vh-lab-filters flex items-center shrink-0">
              {[{ key: 'all', title: 'All' }, ...POTION_POTENCIES].map((option) => {
                const active = filter === option.key;
                const paintSeed = `filter-${option.key}`;
                return (
                  <button key={option.key} type="button" aria-pressed={active} onClick={() => this.filter(option.key)}
                    onMouseEnter={this.hover} className="vh-lab-filter arena-heading relative isolate shrink-0 cursor-pointer whitespace-nowrap">
                    {active ? <PotionPaint key="selected" seed={paintSeed} variant="brush-plate" className="vh-lab-filter-paint vh-lab-row-selection" draw={filterClicked} />
                      : <PotionPaint key="hover" seed={paintSeed} variant="brush-plate" className="vh-lab-filter-paint vh-lab-row-wash" />}
                    <span className="relative">{t(option.title)}</span>
                  </button>
                );
              })}
            </div>
            <div className="arena-heading flex items-center shrink-0" style={{ color: '#978e81', fontSize: '0.9cqw', letterSpacing: '0.04em', gap: '0.85cqw', padding: '0 1.5cqw 0.65cqw 6.5cqw' }}>
              <span className="flex-1">{t('Potion')}</span>
              <span style={{ width: '4.5cqw', textAlign: 'center' }}>{t('Owned')}</span>
              <span style={{ width: '8.5cqw' }}>{t('Crafting cost')}</span>
            </div>
            <div className="flex flex-col min-h-0 overflow-y-auto overflow-x-hidden" data-testid="potion-list" ref={(node) => { this._list = node; }} style={{ gap: '0.15cqw', padding: '0.35cqw 0.75cqw' }}>
              {this.ordered.flatMap((p) => POTION_POTENCIES.filter((variant) => filter === 'all' || variant.key === filter).map((variant) => (
                <PotionRow key={`${p.key}-${variant.key}`} potion={p} state={potionState(skills, alchemist, p.key, variant.key)} alchemist={alchemist}
                  potency={variant.key} selected={p.key === key && variant.key === potency} drawSelection={this.state.drawSelection}
                  onSelect={this.select} onHover={this.hover} />
              )))}
            </div>
          </div>

          {/* The bench. */}
          <div className="relative flex-1 min-w-0 flex flex-col items-center overflow-y-auto">
            {/* A still manuscript plate: bottle and painted background are one image. */}
              <div
                className="relative flex flex-col items-center flex-1 w-full"
              >
                <div className="flex flex-col items-center w-full my-auto shrink-0">
                <div ref={(node) => { this._bottle = node; }} className="relative flex items-center justify-center shrink-0" style={{ width: '22cqw', height: '22cqw' }}>
                  <PotionArtwork potion={key} known={state.known} brew={this.state.brewFx} />
                </div>

                <div className="flex flex-col items-center text-center" style={{ width: '40cqw', maxWidth: '100%', gap: '0.625cqw', marginTop: '-0.5cqw' }}>
                  <ReservedText className="arena-heading" style={{ color: ACCENT, fontSize: '2cqw', lineHeight: 1.3, letterSpacing: '0.02em', textShadow: `0 0 1cqw ${ACCENT}44` }} testId="potion-name"
                    candidates={ALCHEMIST_POTIONS.flatMap((p) => POTION_POTENCIES.map((variant) => potionName(p, variant.key)))}>
                    {potionName(potion, potency)}
                  </ReservedText>
                  <div aria-hidden style={{ width: '9cqw', height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD} 0.6), transparent)` }} />
                  <ReservedText candidates={ALCHEMIST_POTIONS.flatMap((p) => POTION_POTENCIES.map((variant) => potionEffect(p.key, variant.key)))} style={{ color: TEXT_PRIMARY, fontSize: '1.35cqw', lineHeight: 1.45 }}>{potionEffect(potion.key, tier.key)}</ReservedText>
                  <ReservedText candidates={ALCHEMIST_POTIONS.map((p) => t('“{text}”', { text: t(p.flavour) }))} style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.2cqw', lineHeight: 1.5, fontStyle: 'italic' }}>{t('“{text}”', { text: t(potion.flavour) })}</ReservedText>
                </div>

                {/* The recipe: what one brew takes, red where the ledger is short. */}
                <div className="flex flex-col items-center" style={{ width: '40cqw', maxWidth: '100%', marginTop: '0.75cqw' }} data-testid="potion-recipe">
                <div className="flex items-center justify-center" style={{ gap: '1.25cqw', minHeight: '2cqw' }}>
                  <span className="arena-heading" style={{ color: TEXT_BODY, fontSize: '1.1cqw', letterSpacing: '0.06em' }}>{t('Requires')}</span>
                  {Object.entries(cost).map(([h, n]) => (
                    <HumourCount key={h} humour={HUMOUR_BY_KEY[h]} value={n} short={(alchemist?.[h] || 0) < n} size={1.5} labelColor={TEXT_BODY} />
                  ))}
                </div>
                <div style={{ width: '100%', color: TEXT_BODY, fontSize: '1cqw', lineHeight: 1.4, textAlign: 'center', marginTop: '0.35cqw' }}>
                  <ReservedText testId="potion-recipe-status" candidates={[t('Recipe not learned')]}>{!state.recipe ? t('Recipe not learned') : null}</ReservedText>
                  <ReservedText candidates={POTION_POTENCIES.map((variant) => t('Requires {n} learned Alchemist perks.', { n: variant.perks }))}>
                    {!potencyUnlocked ? t('Requires {n} learned Alchemist perks.', { n: tier.perks }) : null}
                  </ReservedText>
                </div>
                </div>

                {/* The alembic: the same forged plate as End Turn, centred under the bottle. */}
                <div className="flex flex-col items-center" style={{ gap: '0.625cqw', marginTop: '0.75cqw' }}>
                  <ForgedButton tone="gold" disabled={!brewable} onClick={this.brew} radius={RADIUS.xl}
                    className="arena-heading w-[12cqw] px-5 py-4 text-[1.5cqw] tracking-wide whitespace-nowrap text-center">
                    {busy === 'brew' ? t('Brewing') : t('Brew')}
                  </ForgedButton>
                  <div aria-hidden style={{ minHeight: '1.5cqw' }} />
                </div>
                </div>
              </div>
          </div>
        </div>
      </div>
    );
  }
}
